import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { grantAnalyticsConsent, denyAnalyticsConsent } from "@/lib/analytics";

const KEY = "getjeweldesigns_cookie_consent";

// PostHog is loaded globally in public/index.html and starts opted-out
// (opt_out_capturing_by_default) — it only captures after acceptance.
// GA4 uses Consent Mode v2 (see analytics.js): it's already loaded for everyone
// and sending cookieless pings; accepting flips it to full analytics, declining
// keeps it cookieless.
function applyConsent(value) {
  try {
    if (value === "accepted") {
      window.posthog?.opt_in_capturing?.();
      grantAnalyticsConsent();
    } else {
      window.posthog?.opt_out_capturing?.();
      denyAnalyticsConsent();
    }
  } catch {}
}

export function getCookieConsent() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = getCookieConsent();
    if (stored === "accepted" || stored === "declined") {
      applyConsent(stored);
    } else {
      setVisible(true);
    }
  }, []);

  const decide = (value) => {
    try { localStorage.setItem(KEY, value); } catch {}
    applyConsent(value);
    setVisible(false);
    // Let other bottom-pinned prompts (e.g. install) know they can appear now.
    try { window.dispatchEvent(new Event("getjeweldesigns:cookie-decided")); } catch {}
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      className="fixed inset-x-0 bottom-0 z-[70] border-t hairline bg-card/95 backdrop-blur-md fade-in"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 md:py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="flex items-start gap-3 flex-1">
          <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1} />
          <p className="text-sm text-muted-foreground leading-relaxed">
            We use essential cookies to run the site and, with your consent, analytics to understand and
            improve it. See our{" "}
            <Link to="/privacy-policy" className="text-primary underline underline-offset-4 hover:text-primary/80">
              Privacy Policy
            </Link>.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            data-testid="cookie-decline"
            onClick={() => decide("declined")}
            className="px-5 py-2.5 text-xs uppercase tracking-widest border hairline hover:border-primary/60 transition-colors duration-300"
          >
            Decline
          </button>
          <button
            data-testid="cookie-accept"
            onClick={() => decide("accepted")}
            className="px-5 py-2.5 text-xs uppercase tracking-widest border border-primary bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-300"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
